import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Modal,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import { getEmailConfig, saveEmailConfig, testEmailConfig } from "../../../services/api/fytnServices";
import { showSuccess, showError, showInfo } from "../../../components/ModernSnackbar";
import Cookies from 'js-cookie';
import Footer from "../../../components/Footer";

// Helper function to get user ID from JWT token
const getUserIdFromToken = () => {
  try {
    const token = Cookies.get('fytn_token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId;
    }
  } catch (error) {
    console.error('Error decoding JWT token:', error);
  }
  return null;
};

const CoachSettings = ({ userData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [emailConfigOpen, setEmailConfigOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // Email configuration state
  const [emailConfig, setEmailConfig] = useState({
    host: 'smtp.gmail.com',
    port: 587,
    secure: false,
    user: '',
    pass: '',
    email: userData?.email || ''
  });
  const [showEmailHelp, setShowEmailHelp] = useState(false);
  const [savedConfig, setSavedConfig] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Settings tabs configuration
  const settingsTabs = [
    {
      id: 0,
      label: "Email Settings",
      icon: <EmailIcon />,
      description: "Configure email SMTP settings"
    }
  ];

  // Load saved configuration on component mount
  React.useEffect(() => {
    const loadSavedConfig = async () => {
      const userId = userData?.userId || userData?.USER_AUTH_ID || getUserIdFromToken() || 2;
      try {
        const response = await getEmailConfig(userId);
        if (response.status === 'success' && response.data) {
          setSavedConfig({
            host: response.data.host,
            port: response.data.port,
            secure: response.data.secure,
            user: response.data.user,
            email: response.data.email,
            isActive: response.data.isActive,
            updatedAt: response.data.updatedAt
          });
          setConfigLoaded(true);
        }
      } catch (error) {
        console.error('Error loading saved config:', error);
      }
    };
    
    loadSavedConfig();
  }, [userData]);

  // Email configuration functions
  const handleEmailConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEmailConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTestEmailConfig = async () => {
    showInfo('Testing SMTP configuration...');
    
    // Get user ID from userData, JWT token, or fallback to 2
    const userId = userData?.userId || userData?.USER_AUTH_ID || getUserIdFromToken() || 2;
    
    try {
      const response = await testEmailConfig(userId, emailConfig);
      
      if (response.status === 'success') {
        showSuccess('SMTP configuration is valid! You can now send emails.');
   
      } else {
        showError(response.message || 'SMTP configuration test failed.');
      }
    } catch (error) {
      showError('SMTP configuration test failed. Please check your settings.');
    }
  };

  const handleSaveEmailConfig = async () => {
    showInfo('Saving email configuration...');
    
    // Get user ID from userData, JWT token, or fallback to 2
    const userId = userData?.userId || userData?.USER_AUTH_ID || getUserIdFromToken() || 2;
    
    try {
      const response = await saveEmailConfig(userId, emailConfig);
      
      if (response.status === 'success') {
        showSuccess('Email configuration saved successfully!');
        
        // Update saved config state
        setSavedConfig({
          host: emailConfig.host,
          port: emailConfig.port,
          secure: emailConfig.secure,
          user: emailConfig.user,
          email: emailConfig.email,
          isActive: true,
          updatedAt: new Date().toISOString()
        });
        setConfigLoaded(true);
        
        // Close modal after successful save
        setTimeout(() => {
          setEmailConfigOpen(false);
        }, 2000);
      } else {
        showError(response.message || 'Failed to save configuration.');
      }
    } catch (error) {
      showError('Failed to save configuration. Please try again.');
    }
  };

  return (
    <>
      <Box sx={{ p: { xs: 1, sm: 2 }, paddingBottom: 5, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            mt: { xs: 8, sm: 4, md: 2 },
          }}
        >
          <Typography variant={isMobile ? "h5" : "h4"} component="h1">
            Settings
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 2 }, 
          height: 'calc(100vh - 200px)',
          flexDirection: { xs: 'column', md: 'row' }
        }}>
          {/* Vertical Settings Sidebar */}
          <Paper 
            sx={{ 
              width: { xs: '100%', md: 280 }, 
              minWidth: { xs: 'auto', md: 280 },
              bgcolor: '#f8f9fa',
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid #e0e0e0', bgcolor: '#fff' }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} color="primary">
                Settings Menu
              </Typography>
            </Box>
            
            <List sx={{ p: 0 }}>
              {settingsTabs.map((tab, index) => (
                <React.Fragment key={tab.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={activeTab === tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      sx={{
                        py: { xs: 1.5, md: 2 },
                        px: { xs: 2, md: 3 },
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                        },
                        '&:hover': {
                          bgcolor: 'rgba(25, 118, 210, 0.08)',
                        },
                      }}
                    >
                      <ListItemIcon 
                        sx={{ 
                          minWidth: { xs: 36, sm: 40 },
                          color: activeTab === tab.id ? 'white' : 'primary.main'
                        }}
                      >
                        {tab.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={tab.label}
                        secondary={tab.description}
                        primaryTypographyProps={{
                          fontSize: { xs: '0.875rem', sm: '0.95rem' },
                          fontWeight: activeTab === tab.id ? 600 : 400,
                        }}
                        secondaryTypographyProps={{
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          color: activeTab === tab.id ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < settingsTabs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>

          {/* Main Content Area */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {activeTab === 0 && (
              <Box>
                <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
                  Email Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Configure your email SMTP settings to send test links to users.
                </Typography>
                
                {savedConfig && configLoaded ? (
                  <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                    <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
                      Current Configuration
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          SMTP Host: {savedConfig.host}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Port: {savedConfig.port}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Email: {savedConfig.email}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Status: {savedConfig.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </Grid>
                      {savedConfig.updatedAt && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Last Updated: {new Date(savedConfig.updatedAt).toLocaleString()}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => setEmailConfigOpen(true)}
                        startIcon={<span>⚙️</span>}
                        size={isMobile ? "small" : "medium"}
                      >
                        Update Configuration
                      </Button>
                    </Box>
                  </Paper>
                ) : (
                  <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      No email configuration found. Please set up your SMTP settings.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => setEmailConfigOpen(true)}
                      startIcon={<span>⚙️</span>}
                      size={isMobile ? "small" : "medium"}
                    >
                      Configure Email Settings
                    </Button>
                  </Paper>
                )}

                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
                    Email Configuration Help
                  </Typography>
                  <Typography variant="body2" paragraph>
                    To send test links via email, you need to configure SMTP settings. Here's how:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" variant="body2" paragraph>
                      <strong>For Gmail:</strong> Use smtp.gmail.com, port 587, and enable "Less secure app access" or use an App Password
                    </Typography>
                    <Typography component="li" variant="body2" paragraph>
                      <strong>For Outlook:</strong> Use smtp-mail.outlook.com, port 587
                    </Typography>
                    <Typography component="li" variant="body2" paragraph>
                      <strong>For other providers:</strong> Check your email provider's SMTP settings
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => setShowEmailHelp(!showEmailHelp)}
                    startIcon={<span>❓</span>}
                    size={isMobile ? "small" : "medium"}
                  >
                    {showEmailHelp ? 'Hide' : 'Show'} Detailed Help
                  </Button>
                  
                  {showEmailHelp && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" paragraph>
                        <strong>Gmail Setup:</strong>
                      </Typography>
                      <Box component="ol" sx={{ pl: 2 }}>
                        <Typography component="li" variant="body2">
                          Go to your Google Account settings
                        </Typography>
                        <Typography component="li" variant="body2">
                          Enable 2-factor authentication
                        </Typography>
                        <Typography component="li" variant="body2">
                          Generate an App Password for "Mail"
                        </Typography>
                        <Typography component="li" variant="body2">
                          Use the App Password instead of your regular password
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Email Configuration Modal */}
      <Modal open={emailConfigOpen} onClose={() => setEmailConfigOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95vw", sm: "90vw", md: 600 },
            maxWidth: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Typography variant={isMobile ? "h6" : "h5"} component="h2" gutterBottom>
            Email Configuration
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SMTP Host"
                name="host"
                value={emailConfig.host}
                onChange={handleEmailConfigChange}
                placeholder="smtp.gmail.com"
                margin="normal"
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SMTP Port"
                name="port"
                type="number"
                value={emailConfig.port}
                onChange={handleEmailConfigChange}
                placeholder="587"
                margin="normal"
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Your Email"
                name="email"
                type="email"
                value={emailConfig.email}
                onChange={handleEmailConfigChange}
                placeholder="your-email@gmail.com"
                margin="normal"
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                name="user"
                value={emailConfig.user}
                onChange={handleEmailConfigChange}
                placeholder="your-email@gmail.com"
                margin="normal"
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password/App Password"
                name="pass"
                type="password"
                value={emailConfig.pass}
                onChange={handleEmailConfigChange}
                placeholder="your-app-password"
                margin="normal"
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="secure"
                    checked={emailConfig.secure}
                    onChange={handleEmailConfigChange}
                    size={isMobile ? "small" : "medium"}
                  />
                }
                label="Use SSL/TLS (usually for port 465)"
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ 
            mt: 3, 
            display: "flex", 
            gap: { xs: 1, sm: 2 }, 
            justifyContent: "flex-end",
            flexDirection: { xs: "column", sm: "row" }
          }}>
            <Button
              variant="outlined"
              onClick={handleTestEmailConfig}
              disabled={!emailConfig.host || !emailConfig.user || !emailConfig.pass}
              startIcon={<span>🧪</span>}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Test Configuration
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEmailConfig}
              disabled={!emailConfig.host || !emailConfig.user || !emailConfig.pass}
              startIcon={<span>💾</span>}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Save Configuration
            </Button>
            <Button
              variant="outlined"
              onClick={() => setEmailConfigOpen(false)}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
      <Footer />
    </>
  );
};

export default CoachSettings; 